import { ChevronRight } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import {
  customPropertyKeys,
  extractFrontmatterYamlFromMarkdown,
  parseFrontmatterYaml,
  readCreatedBy,
  readPropertyTypes,
} from '../properties/frontmatter-yaml';
import { PropertyRow } from '../properties/property-row';
import { effectiveType } from '../properties/property-values';

export function ReaderPropertiesPanel({ markdown }: { markdown: string }) {
  const [open, setOpen] = useState(false);

  const { properties, types, customKeys, createdBy, parseError } =
    useMemo(() => {
      const yamlBody = extractFrontmatterYamlFromMarkdown(markdown);
      const parsed = parseFrontmatterYaml(yamlBody);
      return {
        properties: parsed.properties,
        types: readPropertyTypes(parsed.properties),
        customKeys: customPropertyKeys(parsed.properties),
        createdBy: readCreatedBy(parsed.properties),
        parseError: parsed.ok ? undefined : parsed.error,
      };
    }, [markdown]);

  const count = customKeys.length;
  // Nothing useful to show (and no raw frontmatter to replace) — skip chrome.
  if (!createdBy && count === 0 && !parseError) {
    return null;
  }

  const label = t.app.properties.title({ n: count });
  const bodyId = 'reader-note-properties';

  return (
    <div className="mb-3 shrink-0" data-testid="note-properties">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((previous) => !previous)}
        className="flex min-w-0 max-w-full items-center gap-1 rounded py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronRight
          size={13}
          className={`shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
        <span className="truncate">{label}</span>
      </button>

      {open && (
        <section
          id={bodyId}
          aria-label={t.app.properties.label}
          className="max-h-[45vh] overflow-y-auto pb-2"
        >
          {parseError && (
            <p
              role="alert"
              className="mb-2 px-1.5 text-sm text-amber-700 dark:text-amber-300"
            >
              {t.app.properties.parseError}
            </p>
          )}

          <PropertyRow
            name={t.app.properties.createdBy}
            value={createdBy ?? '—'}
            type="text"
            canEdit={false}
            system
            onCommit={() => undefined}
            onRemove={() => undefined}
            onCancelDraft={() => undefined}
          />

          {customKeys.map((key) => (
            <PropertyRow
              key={key}
              name={key}
              value={properties[key]}
              type={effectiveType(key, properties[key], types)}
              canEdit={false}
              onCommit={() => undefined}
              onRemove={() => undefined}
              onCancelDraft={() => undefined}
            />
          ))}

          {customKeys.length === 0 && (
            <p className="px-1.5 py-1 text-sm text-muted-foreground">
              {t.app.properties.empty}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
